import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiService } from "../services/api";

const formatAmount = (value: unknown) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return "—";
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const statusBadgeClass = (status?: string) => {
  switch (String(status || "").toLowerCase()) {
    case "pending":
      return "bg-amber-50 text-amber-700";
    case "upcoming":
      return "bg-blue-50 text-blue-700";
    case "ongoing":
      return "bg-orange-50 text-orange-700";
    case "delivered":
      return "bg-green-50 text-green-700";
    case "accepted":
      return "bg-green-50 text-green-700";
    case "rejected":
      return "bg-red-50 text-red-700";
    default:
      return "bg-gray-100 text-gray-600";
  }
};

type MisRow = Record<string, any>;

const TABLE_COLUMNS: { key: string; header: string; format?: (v: any) => string }[] = [
  { key: "tripId", header: "Trip Id" },
  { key: "deliveryStatus", header: "Delivery Status" },
  { key: "startDate", header: "Start Date" },
  { key: "day", header: "Day" },
  { key: "vehicleNumber", header: "VehicleNo" },
  { key: "driverName", header: "Driver Name" },
  { key: "vendorApprovalStatus", header: "Vendor Approval Status" },
  { key: "vendorRejectComment", header: "Vendor Reject Comment" },
  { key: "startPoint", header: "Start Point" },
  { key: "endPoint", header: "End Point" },
  { key: "vehicleCategory", header: "Vehicle Category" },
  { key: "scheduledAt", header: "Scheduled At" },
  { key: "startedAt", header: "Started At" },
  { key: "completedAt", header: "Completed At" },
  { key: "dropPoints", header: "Drop Points" },
  { key: "totalKms", header: "Total Kms", format: formatAmount },
  { key: "totalHours", header: "Total Hours", format: formatAmount },
  { key: "extraKm", header: "Extra Km", format: formatAmount },
  { key: "extraHours", header: "Extra Hours", format: formatAmount },
  { key: "extraDrops", header: "Extra Drops" },
  { key: "vendorCost", header: "Cost To Vendor (E)", format: formatAmount },
  { key: "tollCharges", header: "Toll Charges", format: formatAmount },
  { key: "parkingCharges", header: "Parking Charges", format: formatAmount },
  { key: "otherCharges", header: "Other Charges", format: formatAmount },
  { key: "extraKmAmount", header: "Extra Km Amount", format: formatAmount },
  { key: "extraHourAmount", header: "Extra Hours Amount", format: formatAmount },
  { key: "extraDropAmount", header: "Extra Drop Amount", format: formatAmount },
  { key: "extraChargesTotal", header: "Extra Charges Total", format: formatAmount },
  { key: "totalDriverRewardPoints", header: "Total Driver Reward Points", format: formatAmount },
  { key: "rating", header: "Rating" },
  { key: "ratingComment", header: "Rating Comment" },
];

const JDInstantMisReport: React.FC = () => {
  const [rows, setRows] = useState<MisRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const queryParams = useMemo(
    () => ({
      page: currentPage,
      limit: 25,
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(status ? { status } : {}),
      ...(startDate ? { startDate } : {}),
      ...(endDate ? { endDate } : {}),
    }),
    [currentPage, endDate, search, startDate, status],
  );

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await apiService.getJdInstantMisReports(queryParams);
      if (!response.success) {
        throw new Error(response.message || response.error || "Failed to load MIS report");
      }
      const payload = (response as any).data || response;
      setRows(payload.rows || []);
      setTotalPages(payload.pagination?.totalPages || 1);
    } catch (err: any) {
      setError(err?.message || "Failed to load MIS report");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleExport = async () => {
    setExportLoading(true);
    try {
      await apiService.exportJdInstantMisReportsCsv({
        ...(search.trim() ? { search: search.trim() } : {}),
        ...(status ? { status } : {}),
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {}),
      });
    } catch (err: any) {
      setError(err?.message || "Failed to export CSV");
    } finally {
      setExportLoading(false);
    }
  };

  const renderCell = (row: MisRow, column: (typeof TABLE_COLUMNS)[number]) => {
    const raw = row[column.key];
    if (column.key === "tripId" && raw) {
      return (
        <Link to={`/jd-instant/${row._id}`} className="font-medium text-jd-green hover:underline">
          {raw}
        </Link>
      );
    }
    if (column.key === "deliveryStatus" && raw) {
      return (
        <span
          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${statusBadgeClass(raw)}`}
        >
          {raw}
        </span>
      );
    }
    if (column.key === "vendorApprovalStatus" && raw) {
      return (
        <span
          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${statusBadgeClass(raw)}`}
        >
          {raw}
        </span>
      );
    }
    if (column.key === "vendorRejectComment" && raw) {
      return (
        <span className="block max-w-[12rem] truncate text-red-700" title={String(raw)}>
          {raw}
        </span>
      );
    }
    if (column.format) return column.format(raw);
    if (raw === "" || raw == null) return "—";
    return String(raw);
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">JD Instant MIS Report</h1>
          <p className="text-sm text-gray-500">View and export your assigned JD Instant trips.</p>
        </div>
        <button
          type="button"
          onClick={handleExport}
          disabled={exportLoading}
          className="rounded-lg bg-jd-green px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
        >
          {exportLoading ? "Exporting..." : "Export CSV"}
        </button>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Search</label>
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Trip id, vehicle, driver..."
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Status</label>
            <select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="upcoming">Upcoming</option>
              <option value="ongoing">Ongoing</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
              <option value="expired">Expired</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">From date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">To date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
        </div>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                {TABLE_COLUMNS.map((column) => (
                  <th key={column.key} className="px-4 py-3 whitespace-nowrap">
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={TABLE_COLUMNS.length} className="px-4 py-12 text-center text-gray-500">
                    Loading MIS report...
                  </td>
                </tr>
              ) : rows.length ? (
                rows.map((row) => (
                  <tr key={row._id || row.tripId} className="align-top">
                    {TABLE_COLUMNS.map((column) => (
                      <td key={column.key} className="px-4 py-3 text-gray-700 whitespace-nowrap">
                        {renderCell(row, column)}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={TABLE_COLUMNS.length} className="px-4 py-12 text-center text-gray-500">
                    No trips found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 ? (
          <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3">
            <button
              type="button"
              disabled={currentPage <= 1 || loading}
              onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              disabled={currentPage >= totalPages || loading}
              onClick={() => setCurrentPage((page) => page + 1)}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default JDInstantMisReport;
